import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Radio, DollarSign, Smartphone, ShieldCheck, ArrowRight } from 'lucide-react';
import { FlowButton } from '../ui/flow-button';
import './TmsFeatureSection.css';

export const TMS_FEATURES = [
  {
    id: 'dispatch', label: 'Dispatch', icon: Radio,
    title: 'See what is holding up the next action.',
    points: ['Keep assignments, load updates and documents together', 'Make missing information and unresolved holds visible', 'Follow delivery paperwork through to billing review'],
    focus: 'From assignment to billing review.',
  },
  {
    id: 'money', label: 'Money', icon: DollarSign,
    title: 'Follow the records behind billing and pay.',
    points: ['Review the documents supporting each invoice', 'Connect driver pay to load records and agreed terms', 'Track payments and outstanding accounting work'],
    focus: 'From completed work to clear records.',
  },
  {
    id: 'driver', label: 'Driver', icon: Smartphone,
    title: 'Give drivers a clear way to complete their part.',
    points: ['Receive assignments and send load updates', 'Submit documents and inspection information', 'Access assigned training and pay statements'],
    focus: 'From an assignment to the next update.',
  },
  {
    id: 'safety', label: 'Safety', icon: ShieldCheck,
    title: 'Track the issue through its required follow-up.',
    points: ['Review qualification records and expiry dates', 'Connect Safety events with assigned actions and training', 'Show unresolved items and clearance decisions'],
    focus: 'From an open issue to a clear next step.',
  },
];

export default function TmsFeatureSection({ eyebrow }) {
  const [selected, setSelected] = useState(0);
  const tabs = useRef([]);
  const active = TMS_FEATURES[selected];
  const Icon = active.icon;

  const moveFocus = (event, index) => {
    let next;
    if (event.key === 'ArrowRight') next = (index + 1) % TMS_FEATURES.length;
    if (event.key === 'ArrowLeft') next = (index + TMS_FEATURES.length - 1) % TMS_FEATURES.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = TMS_FEATURES.length - 1;
    if (next === undefined) return;
    event.preventDefault();
    setSelected(next);
    tabs.current[next]?.focus();
  };

  return <section id="tms-platform" data-testid="home-tms-platform" className="home-tms" aria-labelledby="home-tms-heading">
    <div className="home-tms__inner">
      {eyebrow}
      <div className="home-tms__intro">
        <h2 id="home-tms-heading">Connect the people behind every load.</h2>
        <p>CarrierFlow is Magellan's transportation management system for US trucking companies. It connects load information, company records and department tasks so Dispatch, Safety, Accounting and drivers can see the information they need and what requires attention.</p>
      </div>

      <div className="home-tms__features">
        <div role="tablist" aria-label="CarrierFlow departments" className="home-tms__tabs">
          {TMS_FEATURES.map((feature, index) => {
            const TabIcon = feature.icon;
            return <button key={feature.id} type="button" role="tab" id={`home-tms-tab-${feature.id}`}
              aria-controls={`home-tms-panel-${feature.id}`} aria-selected={selected === index}
              tabIndex={selected === index ? 0 : -1} ref={element => { tabs.current[index] = element; }}
              onClick={() => setSelected(index)} onKeyDown={event => moveFocus(event, index)}>
              <TabIcon size={18} strokeWidth={1.5} aria-hidden="true" />
              <span>{feature.label}</span><span className="home-tms__tab-number" aria-hidden="true">0{index + 1}</span>
            </button>;
          })}
        </div>

        {TMS_FEATURES.map((feature, index) => <div key={feature.id} role="tabpanel"
          id={`home-tms-panel-${feature.id}`} aria-labelledby={`home-tms-tab-${feature.id}`}
          tabIndex={0} hidden={selected !== index} className="home-tms__panel">
          {selected === index && <>
            <div className="home-tms__feature-heading">
              <p className="home-tms__eyebrow"><Icon size={16} aria-hidden="true" />{active.label} workflow</p>
              <h3>{active.title}</h3>
              <p className="home-tms__focus">{active.focus}</p>
            </div>
            <ol className="home-tms__points" aria-label={`${active.label} capabilities`}>
              {active.points.map((point, pointIndex) => <li key={point}>
                <span className="home-tms__point-number" aria-hidden="true">0{pointIndex + 1}</span>
                <span>{point}</span>
              </li>)}
            </ol>
          </>}
        </div>)}
        <div className="home-tms__feature-footer">
          <p>Keep the information with the task.</p>
          <Link to="/tms" data-testid="home-tms-preview-link" aria-label="Explore CarrierFlow TMS workflows">Explore the workflows <ArrowRight size={16} aria-hidden="true" /></Link>
        </div>
      </div>

      <div className="home-tms__actions">
        <FlowButton to="/tms" data-testid="home-tms-cta">Explore CarrierFlow TMS</FlowButton>
        <FlowButton to="/#contact">Request a private demo</FlowButton>
      </div>
    </div>
  </section>;
}
